import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ListChecks } from 'lucide-react';
import { summarizeQualification } from './summarizeQualification';
export const criterionAnchor = (id)=>`qualification-criterion-${id}`;
const BUCKETS = [
    {
        key: 'strengths',
        bucket: 'strength',
        title: 'Strengths'
    },
    {
        key: 'concerns',
        bucket: 'concern',
        title: 'Concerns'
    },
    {
        key: 'neutral',
        bucket: 'neutral',
        title: 'Assessed, neutral'
    },
    {
        key: 'unknowns',
        bucket: 'unknown',
        title: 'Unknowns'
    },
    {
        key: 'followUps',
        bucket: 'follow-up',
        title: 'Open follow-ups'
    },
    {
        key: 'warnings',
        bucket: 'warning',
        title: 'Warnings'
    }
];
function goTo(id) {
    const el = document.getElementById(criterionAnchor(id));
    el?.scrollIntoView({
        block: 'center'
    });
    el?.focus();
}
/**
 * Evidence-backed rollup of the evaluated answers (SAD-478). A weighted score
 * appears only when the template weights criteria; unknown inputs are shown as
 * unknown. It never sets a disposition — that stays a human decision (QUAL-06).
 */ export function QualificationSummary({ criteria, answers, basis, suggestions }) {
    const s = summarizeQualification({
        criteria,
        answers,
        basis,
        suggestions
    });
    const entry = (bucket, i, n)=>/*#__PURE__*/ _jsxs("li", {
            "data-testid": Testids.qualification.summaryItem,
            "data-bucket": bucket,
            "data-criterion-id": i.id,
            children: [
                /*#__PURE__*/ _jsx("strong", {
                    children: i.label
                }),
                ": ",
                i.text,
                i.evidence && /*#__PURE__*/ _jsxs("span", {
                    className: "text-xs text-muted-foreground",
                    children: [
                        " · evidence: ",
                        i.evidence
                    ]
                }),
                ' ',
                /*#__PURE__*/ _jsx(SpecraButton, {
                    className: "font-normal text-xs text-current underline",
                    onClick: ()=>goTo(i.id),
                    "data-testid": Testids.qualification.summaryReturnLink,
                    "data-criterion-id": i.id,
                    size: "inline",
                    variant: "link",
                    children: "Go to answer"
                })
            ]
        }, `${bucket}-${i.id}-${n}`);
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Qualification summary",
        className: "rounded border p-3 space-y-2",
        "data-testid": Testids.qualification.summary,
        "data-scoring": s.score ? 'on' : 'off',
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 font-medium",
                children: [
                    /*#__PURE__*/ _jsx(ListChecks, {
                        className: "h-4 w-4"
                    }),
                    " Summary"
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Rolls up your answers and their evidence. It does not decide disposition."
            }),
            s.score && /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.qualification.summaryScore,
                "data-score": s.score.percent ?? 'unknown',
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Weighted score:",
                            ' ',
                            /*#__PURE__*/ _jsx("strong", {
                                children: s.score.percent === null ? 'Unknown — no weighted input is known' : `${s.score.percent}%`
                            }),
                            ' ',
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    "(known weight ",
                                    s.score.knownWeight,
                                    " of ",
                                    s.score.totalWeight,
                                    "; unknown inputs are excluded, never counted as 0)"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "text-xs",
                        children: s.score.inputs.map((i)=>/*#__PURE__*/ _jsxs("li", {
                                "data-testid": Testids.qualification.summaryScoreInput,
                                "data-criterion-id": i.id,
                                "data-value": i.value ?? 'unknown',
                                children: [
                                    i.label,
                                    ": ",
                                    i.explanation
                                ]
                            }, i.id))
                    })
                ]
            }),
            BUCKETS.map(({ key, bucket, title })=>/*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs("h5", {
                            className: "font-medium",
                            children: [
                                title,
                                " (",
                                s[key].length,
                                ")"
                            ]
                        }),
                        s[key].length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "None."
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-1",
                            children: s[key].map((i, n)=>entry(bucket, i, n))
                        })
                    ]
                }, key))
        ]
    });
}
